import { type PropsWithChildren, useMemo } from 'react';

import { ApiAlertsRepository } from '@/alerts/infra/repositories/api/api.alerts.repository';
import {
  type Dependencies,
  DependenciesProvider,
} from '@/api/fe-api/contexts/dependencies.context';
import { FeApiAIRepository } from '@/api/fe-api/repositories/fe-api.ai.repository';
import { FeApiAlertNotificationRepository } from '@/api/fe-api/repositories/fe-api.alert-notification.repository';
import { FeApiCapacityEvaluationRepository } from '@/api/fe-api/repositories/fe-api.capacity-evaluation.repository';
import { FeApiSiteMetadataRepository } from '@/api/fe-api/repositories/fe-api.site-metadata.repository';
import { FeApiSiteRepository } from '@/api/fe-api/repositories/fe-api.site.repository';
import { FeApiVehicleRepository } from '@/api/fe-api/repositories/fe-api.vehicle.repository';
import { useAuth } from '@/hooks/use-auth';
import { useFeatureFlag } from '@/hooks/use-feature-flag';
import { useMe } from '@/hooks/use-me';
import { FeApiActivityRepository } from '@/modules/documents/infra/repositories/fe-api/fe-api.activity.repository';
import { FeApiDocumentRepository } from '@/modules/documents/infra/repositories/fe-api/fe-api.document.repository';

type RecordsProviderProps = PropsWithChildren<{}>;

export function FeApiProvider({ children }: RecordsProviderProps) {
  const me = useMe();
  const auth = useAuth();
  const featureFlag = useFeatureFlag();

  const dependencies = useMemo<Dependencies>(
    function () {
      return {
        repositories: {
          ai: new FeApiAIRepository(me, auth),
          alertNotification: new FeApiAlertNotificationRepository(me, auth),
          alerts: new ApiAlertsRepository(auth, me, featureFlag),
          capacityEvaluation: new FeApiCapacityEvaluationRepository(me, auth),
          site: new FeApiSiteRepository(me, auth),
          siteMetadata: new FeApiSiteMetadataRepository(me, auth),
          vehicle: new FeApiVehicleRepository(me, auth),
          documentActivity: new FeApiActivityRepository(me, auth),
          document: new FeApiDocumentRepository(me, auth),
        },
      };
    },
    [auth, me, featureFlag],
  );
  return (
    <DependenciesProvider dependencies={dependencies}>
      {children}
    </DependenciesProvider>
  );
}
